/**
 * Footer for textarea in the discussions feature that allows users to post on
 * videos and articles.
 * The footer contains the submit button, cancel button (for editing posts and
 * accepting clarifications), and a button for toggling formatting options.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries, standardTransition} from "@khan/shared-styles";

import FormattedMarkdown from "#components/formatted-markdown-package/formatted-markdown.tsx";
import {domainColors} from "#content-library-legacy/styles/shared.ts";

import * as sharedDiscussionStyles from "../shared-styles.ts";
import {getTabsStrings} from "../strings.ts";
import type {FeedbackType} from "../types.ts";

import DiscussionFormattingOptions from "./discussion-formatting-options.tsx";

const defaultDomainColor = domainColors("default").domain1;

type Props = {
    tab: FeedbackType;
    submitStatus: "ready" | "submitting" | "failed";
    submitButtonDisabled: boolean;
    canShowFormattingOptions: boolean;
    formattingOptionsExpanded: boolean;
    onToggleFormattingOptionsExpanded?: (arg1: boolean) => void;
    previewExpanded: boolean;
    onTogglePreviewExpanded?: (arg1: boolean) => void;
    content: string;
    // Whether this input field is for submitting an answer.
    isAnswer?: boolean;
    // Whether this input field is for submitting a reply.
    isReply?: boolean;
    // Whether the user is currently editing an existing post.
    editing: boolean;
    // Whether the user is currently accepting a user-submitted clarification.
    acceptingClarification: boolean;
    // For canceling editing the post.
    onCancel?: () => void;
    // For submitting the post (used for new and edited posts).
    onSubmit: () => void;
};

// string helper
// are responses called "answers" or "replies"?
const areResponsesCalledAnswers = (check: string): boolean => {
    return (
        check === "projecthelp" || check === "question" || check === "questions"
    );
};

export default class DiscussionInputFooter extends React.Component<Props> {
    static defaultProps: {
        acceptingClarification: boolean;
        editing: boolean;
    } = {
        editing: false,
        acceptingClarification: false,
    };

    componentDidUpdate(prevProps: Props) {
        const {submitButton} = this;

        // If the post submission failed, we need to refocus on the submit button.
        // When the post was being submitted, the focus was unset because the
        // button was disabled.
        const refocusSubmitButton =
            submitButton &&
            this.props.submitStatus === "failed" &&
            prevProps.submitStatus === "submitting";
        if (refocusSubmitButton) {
            // HACK(diedra): setTimeout with 0 ms just makes sure that the logic
            // inside the function body is run after any other logic that is happening
            // at the same time, which in this case makes sure that the input field
            // is ready to be focused on.
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // TODO: Write a test for this
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => submitButton.current?.focus(), 0);
        }
    }

    submitButton: React.RefObject<HTMLButtonElement> = React.createRef();

    getSubmitButtonCopy(): string {
        const {
            tab,
            isAnswer,
            isReply,
            editing,
            acceptingClarification,
            submitStatus,
        } = this.props;

        // are responses called "answers" or "replies"?
        const responsesAreAnswers = areResponsesCalledAnswers(tab);

        if (submitStatus === "submitting") {
            return t`Saving...`;
        }
        if (editing) {
            return t`Save changes`;
        }
        if (acceptingClarification) {
            return t`Accept clarification`;
        }
        if (isReply) {
            return t`Comment`;
        }
        if (!isAnswer && tab) {
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            return getTabsStrings()[tab].buttonContents;
        }
        if (isAnswer && responsesAreAnswers) {
            return t`Post answer`;
        }
        return t`Post reply`;
    }

    renderFormattingOptionsButton: (buttonId: string) => React.ReactNode = (
        buttonId: string,
    ) => {
        const {onToggleFormattingOptionsExpanded, formattingOptionsExpanded} =
            this.props;
        const newFormattingOptionsExpandedState = !formattingOptionsExpanded;
        const buttonLabel = formattingOptionsExpanded
            ? t`Hide formatting options`
            : t`Show formatting options`;

        return (
            <Button
                kind="tertiary"
                aria-expanded={formattingOptionsExpanded ? "true" : "false"}
                aria-controls={buttonId}
                aria-label={buttonLabel}
                style={(styles.button, styles.formattingButton)}
                onClick={() => {
                    if (onToggleFormattingOptionsExpanded) {
                        onToggleFormattingOptionsExpanded(
                            newFormattingOptionsExpandedState,
                        );
                    }
                }}
            >
                {buttonLabel}
            </Button>
        );
    };

    renderPreviewButton: (buttonId: string) => React.ReactNode = (
        buttonId: string,
    ) => {
        const {onTogglePreviewExpanded, previewExpanded} = this.props;
        const newTogglePreviewExpandedState = !previewExpanded;
        const buttonLabel = previewExpanded ? t`Hide preview` : t`Show preview`;

        return (
            <Button
                kind="tertiary"
                aria-expanded={previewExpanded ? "true" : "false"}
                aria-controls={buttonId}
                aria-label={buttonLabel}
                style={(styles.button, styles.formattingButton)}
                onClick={() => {
                    if (onTogglePreviewExpanded) {
                        onTogglePreviewExpanded(newTogglePreviewExpandedState);
                    }
                }}
            >
                {buttonLabel}
            </Button>
        );
    };

    renderPreviewArea(): React.ReactNode {
        return (
            <Body
                style={[
                    sharedDiscussionStyles.content,
                    styles.previewContainer,
                    this.props.previewExpanded &&
                        styles.previewContainerExpanded,
                ]}
            >
                <FormattedMarkdown string={this.props.content} />
            </Body>
        );
    }

    render(): React.ReactNode {
        const {
            isReply,
            editing,
            acceptingClarification,
            canShowFormattingOptions,
            formattingOptionsExpanded,
            submitButtonDisabled,
            submitStatus,
            onSubmit,
        } = this.props;

        const editingOrAcceptingClarification =
            editing || acceptingClarification;

        const postIsBeingSubmitted = submitStatus === "submitting";

        const buttonContents = this.getSubmitButtonCopy();

        const footerStyles = acceptingClarification
            ? [
                  styles.textareaFooter,
                  styles.textareaFooterAcceptingClarification,
              ]
            : styles.textareaFooter;

        return (
            <Id>
                {(idRoot) => (
                    <View
                        style={[
                            styles.textareaContainerFooter,
                            isReply && styles.textareaReplyFooter,
                        ]}
                    >
                        <View style={footerStyles} aria-live="polite">
                            {!acceptingClarification &&
                                this.renderPreviewButton(
                                    `${idRoot}preview-button`,
                                )}
                            {!acceptingClarification &&
                                this.renderFormattingOptionsButton(
                                    `${idRoot}toggle-button`,
                                )}

                            <View style={styles.buttonContainer}>
                                {editingOrAcceptingClarification && (
                                    <Button
                                        kind="tertiary"
                                        onClick={this.props.onCancel}
                                        style={styles.button}
                                    >
                                        {t`Cancel`}
                                    </Button>
                                )}

                                {editingOrAcceptingClarification && (
                                    <Strut size={8} />
                                )}

                                <Button
                                    disabled={
                                        submitButtonDisabled ||
                                        postIsBeingSubmitted
                                    }
                                    onClick={onSubmit}
                                    spinner={postIsBeingSubmitted}
                                    style={styles.button}
                                    ref={this.submitButton}
                                    aria-label={buttonContents}
                                >
                                    {buttonContents}
                                </Button>
                            </View>
                        </View>

                        <View>{this.renderPreviewArea()}</View>

                        <View
                            style={[
                                styles.formattingOptionsContainer,
                                canShowFormattingOptions &&
                                    formattingOptionsExpanded &&
                                    styles.formattingOptionsContainerExpanded,
                            ]}
                            id={`${idRoot}toggle-button`}
                        >
                            {canShowFormattingOptions &&
                                formattingOptionsExpanded && (
                                    <DiscussionFormattingOptions />
                                )}
                        </View>
                    </View>
                )}
            </Id>
        );
    }
}

const styles = StyleSheet.create({
    textareaContainerFooter: {
        padding: "12px 16px",
        border: sharedDiscussionStyles.border,
        borderTop: "none",
        borderBottomRightRadius: 4,
        borderBottomLeftRadius: 4,
    },

    textareaFooter: {
        flexDirection: "row",
        justifyContent: "space-between",

        [mediaQueries.xsOrSmaller]: {
            flexDirection: "column",
        },
    },

    textareaReplyFooter: {
        border: "none",
        paddingLeft: 0,
        paddingRight: 0,
    },

    textareaFooterAcceptingClarification: {
        justifyContent: "flex-end",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    submitButtonContents: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    button: {
        [mediaQueries.xsOrSmaller]: {
            width: "calc(100% + 60px)",
        },
    },

    // eslint-disable-next-line react-native/no-unused-styles
    submitButtonSpinner: {
        marginRight: 8,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    notAButton: {
        ...sharedDiscussionStyles.notAButton,
    },

    // eslint-disable-next-line react-native/no-unused-styles
    footerSecondaryButton: {
        color: defaultDomainColor,
        marginRight: 16,

        ":hover": {
            textDecoration: "underline",
        },

        [mediaQueries.xsOrSmaller]: {
            width: "100%",
            marginBottom: 16,
            marginRight: 0,
        },
    },

    previewContainer: {
        display: "none",
    },

    previewContainerExpanded: {
        borderTop: sharedDiscussionStyles.border,
        borderBottom: sharedDiscussionStyles.border,
        boxSizing: "border-box",
        marginTop: 16,
        marginBottom: 16,
        display: "block",
        minHeight: sharedDiscussionStyles.textareaMinHeight,
        transition: `min-height ${standardTransition}`,
        overflow: "auto",
    },

    formattingOptionsContainer: {
        display: "none",
    },

    formattingOptionsContainerExpanded: {
        display: "flex",
    },

    // eslint-disable-next-line react-native/no-unused-styles
    formattingLabel: {
        fontWeight: "bold",
    },

    formattingButton: {
        [mediaQueries.xsOrSmaller]: {
            width: "100%",
        },
    },

    buttonContainer: {
        flexDirection: "row",
    },
});
